<!-- @format -->

<template>
	<el-container v-if="isShow" class="pform-main-container">
		<el-container class="center-container">
			<el-header class="center-header">
				<widget-toolbar
					ref="toolbarRef"
					:other-func="otherFunc"
					:name="name"
					:is-mobile="isMobile"
					@on-save="(...args) => emit('on-save', ...args)"
					@on-save-error="onSaveError"
				>
					<template #toolbar-save="item">
						<slot name="toolbar-save" v-bind="item" />
					</template>
					<template #toolbar-preview="item">
						<slot name="toolbar-preview" v-bind="item" />
					</template>
					<template #toolbar-right>
						<slot name="toolbar-right" />
					</template>
					<template #preview-top>
						<slot name="preview-top" />
					</template>
					<template #preview-bottom>
						<slot name="preview-bottom" />
					</template>
				</widget-toolbar>
			</el-header>
			<el-main class="center-main">
				<widget-form
					ref="widgetFormRef"
					:default-widget-datas="defaultWidgetDatas"
					:default-form-attribute="defaultFormAttribute"
					:is-field-to-container="isFieldToContainer"
					:is-container-to-container="isContainerToContainer"
					:can-delete="canDelete"
					:can-sort="canSort"
					:can-drag-in="canDragIn"
					:name="name"
					:init-code="initCode"
					:is-mobile="isMobile"
					@on-delete="emit('on-delete', $event)"
					@on-select-callback="onSelectCallback"
				/>
			</el-main>
		</el-container>

		<el-aside class="setting-container">
			<el-tabs v-model="activeTab" stretch class="setting-container-tabs">
				<el-tab-pane label="组件库" name="0">
					<Fields :can-drag="canDrag" />
				</el-tab-pane>
				<el-tab-pane v-if="canTemplate" label="模版" name="1">
					<template-forms :template-list="templateList" @on-template-change="emit('on-template-change', $event)" />
				</el-tab-pane>
				<el-tab-pane label="组件设置" name="2">
					<field-config
						v-if="activeTab === '2'"
						:can-change-field="canChangeField"
						@on-attribute-change="emit('on-attribute-change', $event)"
					/>
				</el-tab-pane>
				<el-tab-pane v-if="canFormAttribute" label="表单设置" name="3">
					<form-config v-if="activeTab === '3'" />
				</el-tab-pane>
			</el-tabs>
		</el-aside>
	</el-container>
	<div v-else v-loading="true" style="height: 100%" />
</template>

<script setup lang="ts">
	import { useBaseConfigStoreWithOut } from '../config/widget';
	import { useDragStoreWithOut } from '../store';
	import ScriptList from '../utils/script';
	import Fields from './left-aside/fields/index.vue';
	import TemplateForms from './left-aside/template/index.vue';
	import WidgetToolbar from './widget-toolbar/index.vue';
	import WidgetForm from './widget-form/index.vue';
	import FieldConfig from './right-aside/field/index.vue';
	import FormConfig from './right-aside/form/index.vue';
	import type { SetBaseConfig, FieldsType, ScriptFuncType, TemplateType } from '../types';

	interface Props {
		setBaseConfig?: SetBaseConfig;
		otherFunc?: ScriptFuncType;
		isFieldToContainer?: boolean;
		isContainerToContainer?: boolean;
		defaultWidgetDatas?: FieldsType[];
		defaultFormAttribute?: Record<string, any>;
		canDelete?: boolean | ((a: any) => boolean);
		canSort?: boolean | ((a: any) => boolean);
		canDragIn?: boolean | ((a: any) => boolean);
		canChangeField?: boolean | ((a: any) => boolean);
		canDrag?: boolean;
		name?: string;
		canTemplate?: boolean;
		templateList?: TemplateType[];
		initCode?: boolean | ((a: any) => string);
		canFormAttribute?: boolean;
		isMobile?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		canDelete: true,
		canSort: true,
		canDrag: true,
		canDragIn: true,
		canChangeField: true,
		initCode: true,
		canFormAttribute: true,
		isContainerToContainer: true,
	});

	const emit = defineEmits(['on-delete', 'on-attribute-change', 'on-save', 'on-template-change']);

	const toolbarRef = ref<InstanceType<typeof WidgetToolbar> | null>(null);
	const widgetFormRef = ref<InstanceType<typeof WidgetForm> | null>(null);

	const validateFieldAttribute = computed(() => toolbarRef.value?.validateFieldAttribute);

	const widgetDatas = computed(() => widgetFormRef.value?.widgetDatas);
	const formAttribute = computed(() => widgetFormRef.value?.formAttribute);

	const dragStore = useDragStoreWithOut(props.name);

	const activeTab = ref('0');

	const isShow = ref(false);

	onBeforeMount(async () => {
		isShow.value = false;
		const baseConfigStore = useBaseConfigStoreWithOut(props.name);
		baseConfigStore.$reset();
		dragStore.$reset();
		props.setBaseConfig && (await baseConfigStore.setState(props.setBaseConfig));
		isShow.value = true;
	});

	const scriptConfig = new ScriptList(ref({}), ref([]), props.otherFunc);

	const onSaveError = () => {
		activeTab.value = '2';
	};

	const onSelectCallback = () => {
		activeTab.value = '2';
	};

	provide('scriptConfig', scriptConfig);

	provide('name', props.name);

	defineExpose({ validateFieldAttribute, widgetDatas, formAttribute, dragStore });
</script>

<style scoped lang="scss">
	.pform-main-container {
		height: 100%;

		.field-container {
			width: 220px !important;
			padding: 0 5px;
		}

		.center-container {
			flex: 1;
			border-right: 1px solid var(--el-border-color-light);

			.center-header {
				height: 40px !important;
				border-bottom: 2px solid var(--el-border-color-light);
				margin: 0 5px;
				padding: 0px;
			}

			.center-main {
				padding: 9px 0;
			}
		}

		.setting-container {
			width: 320px !important;
			padding: 0 5px;

			.setting-container-tabs {
				height: 100%;

				:deep(.el-tabs__content) {
					height: calc(100% - 54px);
					overflow-y: scroll;
				}
			}
		}
	}
</style>
